import React, { forwardRef, useState } from 'react'
import { Divider } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import { user } from 'ampersand-app'
import lang from '@/lang'
import ClientConfig from '@/modules/client/config'
import { switchTenant } from '@/redux/ducks/user/info'
import { fetchClientData } from '@/redux/ducks/client/detail'

import { Title } from '@/modules/base/components/texts'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import ClientOverViewEditDrawer from '@/modules/client/components/detail/client-overview-edit-drawer'
import { ButtonIconSquare } from '@/modules/base/components/buttons'

import IconEdit from '@assets/images/icons/pencil.svg'
import BuildingIcon from '@assets/images/icons/building.svg'
import CompanyIcon from '@assets/images/icons/company.svg'
import UserIcon from '@assets/images/icons/user.svg'
import NumberIcon from '@assets/images/icons/number.svg'
import MailIcon from '@assets/images/icons/mail.svg'
import GroupIcon from '@assets/images/icons/group.svg'
import IconCheck from '@assets/images/icons/check.svg'
import IconDelete from '@assets/images/icons/trashcan.svg'
import IconCross from '@assets/images/icons/close.svg'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { CLIENT_INVITATION_STATUS } from '@/modules/client/_helpers/getStatusLabel'
import { TaxConsultantService } from '@/modules/settings/components/tax-consultant/taxConsultantService'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import CreateModal from '@/modules/client/components/list/create/modal'
import { ConfirmModal } from '@/modules/base/components/modal'
import { ROLES } from '@/modules/base/util/constants'
import request from '@/modules/base/util/request'
import { useNavigate } from 'react-router'

const { resourceUrl, clientDetail: { jumpMarkLinks } } = ClientConfig

const useStyles = makeStyles((theme) => ({
  wrapper: {
    backgroundColor: theme.white,
    width: '100%',
    height: 'min-content',
    border: theme.border,
    borderRadius: theme.borderRadius,
    padding: 30,
  },
  details: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',

    '& div': {
      display: 'flex',
      alignItems: 'center',
      width: 'calc(50% - 20px)',
      padding: '14px 0',
      boxSizing: 'border-box',
      borderBottom: theme.border,
      marginBottom: 10,

      '& svg': {
        height: '15px',
        width: '15px',
        fill: '#374A67',
      },
      '&:last-child': {
        borderBottom: 'none',
        margin: 0,
      },
    },
  },
  detailIcon: {
    marginRight: 12,
    color: '#334a6a',
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: '10px 0',
  },
  subHeading: {
    fontSize: theme.fontSizes.smedium,
    fontWeight: theme.fontWeights.medium,
  },
  divider: {
    margin: '30px 0',
  },
  tooltip: {
    color: theme.textColor,
  },
  tooltipHeading: {
    fontSize: theme.fontSizes.default,
    fontWeight: theme.fontWeights.bold,
    marginBottom: 10,
  },
  tooltipBody: {
    fontSize: theme.fontSizes.smaller,
  },
  offlineButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  resendEmailContent: {
    gap: 15,
    display: 'flex',
    flexDirection: 'column',
  },
}))

const ClientOverview = forwardRef(({ client }, ref) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {
    id,
    clientNumber,
    companyName,
    companyForm,
    inviteeTenantId,
    inviterTenantId,
    inviterStatus,
    inviteeStatus,
    clerks = [],
    responsibleUser,
  } = client
  const [openClientCreationModal, setOpenClientCreationModal] = useState(false)
  const [openEditDrawer, setOpenEditDrawer] = useState(false)
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false)
  const [openEndCollaborationConfirmation, setOpenEndCollaborationConfirmation] = useState(false)

  const currentTenant = useSelector(state => state.tenant.info)
  const getClientId = () => {
    if (currentTenant.id === inviteeTenantId) return inviterTenantId
    if (currentTenant.id === inviterTenantId) return inviteeTenantId
  }

  const isOwner = (user?.roles || []).includes(ROLES.OWNER_ROLE)
  const isAdmin = (user?.roles || []).includes(ROLES.TAX_CONSULTANT_ROLE_ADMIN)
  const isClerk = clerks.map(({ user: { id } }) => id).includes(user?.userId)
  const isReadOnly = !(isAdmin || isClerk || isOwner)

  const EMPTY_FIELD = lang.generics.emptyProp
  const contactUserName = (responsibleUser?.firstName && responsibleUser?.lastName) ? `${responsibleUser?.firstName} ${responsibleUser?.lastName}` : EMPTY_FIELD
  const contactUserEmail = responsibleUser?.email || EMPTY_FIELD
  const isInviter = currentTenant.id === inviterTenantId
  const ownConnectionStatus = isInviter ? inviterStatus : inviteeStatus
  const isOfflineClient = client?.inviteeStatus === CLIENT_INVITATION_STATUS.OFFLINE

  const clerkNames = clerks
    .map(clerk => clerk.label)
    .filter(name => name)
    .join(', ')

  const loginToClient = () => {
    dispatch(switchTenant({ tenantId: getClientId() }, () => {
      navigate('/')
    }))
  }

  const connectClient = () => {
    setOpenClientCreationModal(true)
  }

  const deleteClientClick = async() => {
    try {
      await request(`${resourceUrl}/${client.id}`, {
        method: 'DELETE',
        auth: true,
      })
      navigate('/clients')
    } catch (e) {
      toast.showToast({ type: 'error', message: lang.taxConsultant.errors.enable })
    }

    setOpenDeleteConfirmation(false)
  }

  const endCollaborationClick = async() => {
    try {
      await TaxConsultantService.disconnect(client.id)
      navigate('/clients')
    } catch (e) {
      toast.showToast({ type: 'error', message: lang.taxConsultant.errors.revoke })
    }

    setOpenEndCollaborationConfirmation(false)
  }

  const canEndCollaboration = isOwner || isAdmin

  const reinviteClient = async() => {
    const clerkIds = clerks.map(clerk => clerk.user?.id).filter(Boolean)
    try {
      if (client.isReadOnly) {
        await TaxConsultantService.restoreCollaboration(id, clerkIds)
      } else {
        await TaxConsultantService.resendCollaborationInvitation(id)
      }
      toast.showToast({ message: lang.taxConsultantControl.pending.resentInvitation })
      dispatch(fetchClientData(id))
    } catch (e) {
      toast.showToast({ type: 'error', message: lang.taxConsultant.errors.invite })
    }
  }

  const handleSave = async(data) => {
    await request(`${resourceUrl}/${client.id}`, {
      method: 'PUT',
      auth: true,
      data,
    })
    dispatch(fetchClientData(id))
  }

  const handleConnectionClick = async() => {
    try {
      await TaxConsultantService.setConnectionStatus(
        id,
        ownConnectionStatus === CLIENT_INVITATION_STATUS.ACCEPTED
          ? CLIENT_INVITATION_STATUS.PAUSED
          : CLIENT_INVITATION_STATUS.ACCEPTED,
      )
      dispatch(fetchClientData(id))
    } catch (e) {
      toast.showToast({ type: 'error', message: lang.taxConsultant.errors.enable })
    }
  }

  const resendInvitationEmail = async() => {
    try {
      await TaxConsultantService.resendCollaborationInvitation(
        client.id,
      )
      toast.showToast({ type: 'success', message: lang.taxConsultant.resendInvite })
    } catch (e) {
      toast.showToast({ type: 'error', message: lang.taxConsultant.errors.invite })
    }
  }

  const getButtonLabelByState = () => {
    if (isInviter) {
      // this tax tenant hast requested the connection .. possible values is accepted or paused
      switch (inviterStatus) {
        case CLIENT_INVITATION_STATUS.ACCEPTED: return lang.taxConsultant.revoke
        case CLIENT_INVITATION_STATUS.PAUSED: return lang.taxConsultant.reenable
      }

    } else {
      switch (inviteeStatus) {
        case CLIENT_INVITATION_STATUS.ACCEPTED: return lang.taxConsultant.revoke
        case CLIENT_INVITATION_STATUS.REQUESTED:
        case CLIENT_INVITATION_STATUS.PAUSED: return lang.taxConsultant.reenable
      }
    }
    return ''
  }

  const getAcceptButtonReadOnlyByState = () => {
    if (isInviter) {
      // this tax tenant hast requested the connection .. possible values is accepted or paused
      switch (inviterStatus) {
        case CLIENT_INVITATION_STATUS.ACCEPTED:
        case CLIENT_INVITATION_STATUS.PAUSED:
          return false
        default:
          return true
      }

    } else {
      switch (inviteeStatus) {
        case CLIENT_INVITATION_STATUS.ACCEPTED:
        case CLIENT_INVITATION_STATUS.REQUESTED:
        case CLIENT_INVITATION_STATUS.PAUSED:
          return false
        default:
          return true
      }
    }
  }

  const resendEmailContent = (
    <div className={classes.resendEmailContent}>
      <div>
        {lang.clientDetail.widgets.overviewWidget.clientRequested}
      </div>
      <div>
        <ButtonIconSquare
          label={lang.clientDetail.widgets.overviewWidget.resendInvitationButton}
          onClick={resendInvitationEmail}
        />
      </div>
    </div>
  )

  const getStatusByState = () => {
    if (isInviter) {
      // this tax tenant hast requested the connection .. possible values is accepted or paused
      switch (inviterStatus) {
        case CLIENT_INVITATION_STATUS.REQUESTED: return resendEmailContent
        case CLIENT_INVITATION_STATUS.PAUSED: return lang.clientDetail.widgets.overviewWidget.clientPaused
        case CLIENT_INVITATION_STATUS.ACCEPTED: {
          switch (inviteeStatus) {
            case CLIENT_INVITATION_STATUS.REQUESTED: return resendEmailContent
            case CLIENT_INVITATION_STATUS.PAUSED: return lang.clientDetail.widgets.overviewWidget.clientPaused
          }
        }
      }

    } else {
      switch (inviteeStatus) {
        case CLIENT_INVITATION_STATUS.REQUESTED:
        case CLIENT_INVITATION_STATUS.PAUSED: return lang.clientDetail.widgets.overviewWidget.activate
        case CLIENT_INVITATION_STATUS.ACCEPTED: {
          switch (inviterStatus) {
            case CLIENT_INVITATION_STATUS.REQUESTED: return lang.clientDetail.widgets.overviewWidget.clientRequested
            case CLIENT_INVITATION_STATUS.PAUSED: return lang.clientDetail.widgets.overviewWidget.clientPaused
          }
        }
      }
    }
    return ''
  }

  const tooltipContent = (
    <div className={classes.tooltip}>
      <div className={classes.tooltipHeading}>{lang.clientDetail.widgets.overviewWidget.headline}</div>
      <div className={classes.tooltipBody}>{lang.clientDetail.widgets.overviewWidget.tooltip}</div>
    </div>
  )

  const loginButton = (
    <div className={classes.buttonWrapper}>
      <ButtonIconSquare
        icon={IconCheck}
        label={isOfflineClient ? lang.clientDetail.widgets.overviewWidget.offlineInviteButton : lang.clientDetail.widgets.overviewWidget.loginButton}
        onClick={isOfflineClient ? connectClient : loginToClient}
        disabled={!client.isAllowed || isReadOnly}
      />
    </div>
  )

  const offlineComponents = isOfflineClient && (
    <div className={classes.offlineButtons}>
      <RichTooltip content={tooltipContent} placement='right'>
        {loginButton}
      </RichTooltip>
      <div className={classes.buttonWrapper}>
        <ButtonIconSquare
          icon={IconDelete}
          color='red'
          label={lang.clientDetail.widgets.overviewWidget.deleteClient}
          onClick={() => setOpenDeleteConfirmation(true)}
        />
      </div>
    </div>
  )

  const loginControls = client?.isAllowed || isOfflineClient
    ? (
      <>
        <Divider className={classes.divider} />
        <p className={classes.subHeading}>
          {lang.clientDetail.widgets.overviewWidget[isOfflineClient ? 'offlineHeadline' : 'loginHeadline']}
        </p>
        <p>{lang.clientDetail.widgets.overviewWidget[isOfflineClient ? 'offlineDescription' : 'loginDescription']}</p>
        {isOfflineClient
          ? offlineComponents
          : loginButton}

      </>
    )
    : !client.isReadOnly
      ? (
        <p>
          <Divider className={classes.divider} />
          <p className={classes.subHeading}>{lang.clientDetail.widgets.overviewWidget.invitedHeadline}</p>
          {getStatusByState()}
        </p>
      )
      : (
        <p>
          <Divider className={classes.divider} />
          <p className={classes.subHeading}>{lang.clientDetail.widgets.overviewWidget.disconnected}</p>
          <div className={classes.buttonWrapper}>
            <ButtonIconSquare
              label={lang.clientDetail.widgets.overviewWidget.resendInvitationButton}
              onClick={reinviteClient}
              disabled={!canEndCollaboration}
            />
          </div>
        </p>
      )
  return (
    <div ref={ref} className={classes.wrapper} id={jumpMarkLinks.overview}>
      <Title icon={BuildingIcon} className={classes.titleClass} size='small20'>{lang.clientDetail.widgets.overviewWidget.headline}</Title>
      <p>{lang.clientDetail.widgets.overviewWidget.description}</p>

      <div className={classes.details}>
        <div><SVGIcon className={classes.detailIcon} svg={CompanyIcon} height={15} width={15} />{lang.createClient.companyForm[companyForm] || EMPTY_FIELD}</div>
        <div><SVGIcon className={classes.detailIcon} svg={UserIcon} height={15} width={15} />{contactUserName}</div>
        <div><SVGIcon className={classes.detailIcon} svg={NumberIcon} height={15} width={15} />{clientNumber || EMPTY_FIELD}</div>
        <div><SVGIcon className={classes.detailIcon} svg={MailIcon} height={15} width={15} />{contactUserEmail}</div>
        <div><SVGIcon className={classes.detailIcon} svg={GroupIcon} height={15} width={15} />{clerkNames.length ? clerkNames : EMPTY_FIELD}</div>
      </div>
      {!client.isReadOnly && (<div className={classes.buttonWrapper}>
        <ButtonIconSquare
          icon={IconCross}
          onClick={() => setOpenEndCollaborationConfirmation(true)}
          label={lang.taxConsultant.disconnect}
          disabled={!canEndCollaboration || isOfflineClient}
        />
        <ButtonIconSquare
          icon={IconEdit}
          label={lang.editButtonLabel}
          disabled={isReadOnly}
          onClick={() => setOpenEditDrawer(true)} />
      </div>)
      }

      {loginControls}
      <ClientOverViewEditDrawer client={client} open={openEditDrawer} onSubmit={handleSave} onClose={() => setOpenEditDrawer(false)} />
      {openClientCreationModal && <CreateModal open={openClientCreationModal} onClose={() => setOpenClientCreationModal(false)} invitationMode={true} clientData={client} />}

      <ConfirmModal
        open={openDeleteConfirmation}
        title={lang.clientDetail.widgets.overviewWidget.deleteClient}
        description={lang.clientDetail.widgets.overviewWidget.deleteClientDescription(companyName)}
        onConfirm={deleteClientClick}
        onAbort={() => setOpenDeleteConfirmation(false)}
        confirmButtonLabel={lang.generics.yes}
        abortButtonLabel={lang.generics.no}
      />

      <ConfirmModal
        open={openEndCollaborationConfirmation}
        title={lang.clientDetail.widgets.overviewWidget.endCollaborationTitle}
        description={lang.clientDetail.widgets.overviewWidget.endCollaborationDescription()}
        onConfirm={endCollaborationClick}
        onAbort={() => setOpenEndCollaborationConfirmation(false)}
        confirmButtonLabel={lang.generics.yes}
        abortButtonLabel={lang.generics.no}
      />

    </div>
  )
})

ClientOverview.propTypes = {
  client: PropTypes.object,
}

ClientOverview.defaultProps = {
  client: {},
}

export default ClientOverview
